import type { FC } from 'react'
import type { RouteComponentProps } from 'react-router-dom'
import {
	Container,
	Heading,
	Text,
	Box,
	Skeleton,
	SkeletonText,
	Tag,
} from '@chakra-ui/react'
import {
	ContentSection,
	ContentSectionHeading,
	SingleLayout,
	SingleHeader,
	SingleHero,
	SingleBody,
} from '@alexanderathoodly/ui-library'
import {
	SIZE_1070,
	SIZE_410,
	SIZE_290,
	PLACEHOLDER_SRC,
} from '@yta/image-proxy'
import { PublicPageLayout, ErrorPage } from '~/layouts/pages'
import { TestimonialSection } from './testimonials'
import Map from './map'
import { useYtaQuery, providerById } from '@alexanderathoodly/data-models'
import type { StatusError } from '@alexanderathoodly/data-models'
import NotFoundPageLayout from '../notfound/layout'
import { ImgProxy } from '~/utils/imgproxy'
import { Temporal } from '@js-temporal/polyfill'
import { capitalizeFirstLetter } from '~/utils/text'
import { useTranslation } from 'react-i18next'
import LocationsSection from './locations'
import { CoworkingOperatorHelmet } from './helmet'

const CoworkingOperatorLayout: FC<RouteComponentProps<{ id: string }>> = ({
	match,
}) => {
	const { t, i18n } = useTranslation()

	const providerQuery = useYtaQuery(providerById({ id: match.params.id }))

	if (providerQuery.isLoading || providerQuery.isIdle) {
		return (
			<PublicPageLayout>
				<Container maxW="container.lg" minH={2000}>
					<SingleLayout>
						<SingleHeader>
							<Skeleton h={10} />
						</SingleHeader>
						<SingleHero position="relative">
							<Skeleton h={52} />
						</SingleHero>
						<SingleBody>
							<SkeletonText noOfLines={6} spacing={4} mb={14} />
							<SkeletonText noOfLines={6} spacing={4} />
						</SingleBody>
					</SingleLayout>
				</Container>
			</PublicPageLayout>
		)
	}
	if (providerQuery.isError) {
		if ((providerQuery.error as StatusError).statusCode == 404) {
			return <NotFoundPageLayout />
		}
		return <ErrorPage error={providerQuery.error as Error} />
	}
	const provider = providerQuery.data
	const foundingDate =
		provider.founding_date && Temporal.PlainDate.from(provider.founding_date)

	return (
		<PublicPageLayout>
			<CoworkingOperatorHelmet provider={provider} />
			<Container maxW="container.lg" minH={2000}>
				<SingleLayout
					gridTemplateRows="min-content 5.5rem 5.5rem 1fr"
					gridTemplateColumns={{ base: '1fr', lg: '66% 1fr' }}
				>
					<SingleHeader>
						<Heading as="h1">{provider.name}</Heading>
					</SingleHeader>
					<SingleHero position="relative" height="fit-content">
						<ImgProxy
							src={provider.locations[0]?.images[0] || PLACEHOLDER_SRC}
							sizeSet={{ base: SIZE_290, md: SIZE_410, lg: SIZE_1070 }}
							w="100%"
							css={{ aspectRatio: '18 / 10' }}
							alt={provider.name}
						/>
					</SingleHero>
					<SingleBody>
						<ContentSection
							display="flex"
							gap={6}
							flexWrap="wrap"
							borderTop="none"
							alignItems="center"
						>
							<Box>
								<Text
									fontWeight="semibold"
									fontSize={{ base: 'xl', md: '2xl' }}
								>
									{provider.locations
										.reduce((s, { size }) => s + size, 0)
										.toLocaleString('sv-SE')}
									m²
								</Text>
								<Text fontSize="medium" color="grey.500">
									{t('Total kontorsyta')}
								</Text>
							</Box>
							{provider.locations.length > 0 && (
								<Box>
									<Text
										fontWeight="semibold"
										fontSize={{ base: 'xl', md: '2xl' }}
									>
										{provider.locations.length}
									</Text>
									<Text fontSize="medium" color="grey.500">
										{t('Antal anläggningar')}
									</Text>
								</Box>
							)}
							{foundingDate && (
								<Tag
									borderRadius="full"
									height="max-content"
									alignSelf="center"
									py={3}
									px={5}
								>
									{`${t('Startades')} ${capitalizeFirstLetter(
										foundingDate.toLocaleString(`${i18n.language}-SE`, {
											year: 'numeric',
											month: 'short',
										})
									)}`}
								</Tag>
							)}
							<Box>
								<ImgProxy
									src={provider.logo}
									sizeSet={{ base: SIZE_290 }}
									maxH="6rem"
									maxW="6rem"
								/>
							</Box>
						</ContentSection>
						<ContentSection>
							<ContentSectionHeading>
								{`${t('Om')} ${provider.name}`}
							</ContentSectionHeading>
							<Text whiteSpace="pre-wrap" color="grey.500">
								{i18n.language != 'en'
									? provider.operator_page_description_about
									: provider.operator_page_description_about_en}
							</Text>
						</ContentSection>
						<ContentSection borderTop="none">
							<ContentSectionHeading>
								{`${t('Varför')} ${provider.name}`}
							</ContentSectionHeading>
							<Text whiteSpace="pre-wrap" color="grey.500">
								{i18n.language != 'en'
									? provider.operator_page_description_why
									: provider.operator_page_description_why_en}
							</Text>
						</ContentSection>
						<TestimonialSection provider={provider} />
						<ContentSection>
							<ContentSectionHeading>
								{t('Alla {{provider}} anläggningar', {
									provider: provider.name,
								})}
							</ContentSectionHeading>
							<Map locations={provider.locations} />
						</ContentSection>
					</SingleBody>
				</SingleLayout>
				<LocationsSection provider={provider} />
			</Container>
		</PublicPageLayout>
	)
}

export default CoworkingOperatorLayout
